import React, { useEffect, useRef, useState } from "react";
import AuthService from "../../../api/services/AuthService";
import "./Referral.css";
import moment from "moment";
import LoaderHelper from "../../../customComponent/Loading/LoaderHelper";

const ReferralTree = ({ userId }) => {
    const [treeRoot, setTreeRoot] = useState({});
    const [flatInvestments, setFlatInvestments] = useState([]);
    const [totalReferralInvestments, setTotalReferralInvestments] = useState(0);
    const [payouts, setPayouts] = useState([]);

    const flattenTreeInvestments = (node) => {
        let investments = [];

        // Add self investments
        if (node?.total_invested_amount?.length > 0) {
            node.total_invested_amount.forEach((inv) => {
                investments.push({
                    userName: node.name,
                    email: node.emailId,
                    level: node.level || 0,
                    amount: inv.amount?.$numberDecimal || inv.amount,
                    currency: inv.currency,
                    investmentId: inv.investmentId,
                    status: inv.status,
                    self_roi_percent: inv.self_roi_percent,
                    your_upline_percent: inv.your_upline_percent,
                    date: (inv.payoutDate) ? inv.payoutDate : inv.createdAt,
                    type: inv.type || (node.level === 0 ? 'self' : 'Investor')
                });
            });
        }
        console.log('Current Node Investments:', investments);

        // Recursively handle referrals
        if (node?.referrals?.length > 0) {
            node.referrals.forEach((child) => {
                investments = [...investments, ...flattenTreeInvestments(child)];
            });
        }

        return investments;
    };


    useEffect(() => {
        const loadInitialTree = async () => {
            try {
                LoaderHelper.loaderStatus(true);
                if (!userId) return;

                const referrals = await AuthService?.get_downline?.(userId, 1, userId);
                const total_referrals_investments = flattenTreeInvestments(referrals?.data).filter(x => x.type !== 'self').reduce((sum, inv) => sum + parseFloat(inv.amount || 0), 0);
                setTotalReferralInvestments(total_referrals_investments);
                console.log('referrals data', referrals?.data);
                if (referrals?.data) {
                    setTreeRoot(referrals?.data);
                    const flat = flattenTreeInvestments(referrals?.data);
                    flat.sort((a, b) => new Date(a.date) - new Date(b.date)); // Sort by date ascending
                    setFlatInvestments(flat);
                }
            } catch (error) {
                console?.error?.("Failed to load referral data:", error);
            } finally { LoaderHelper.loaderStatus(false) }
        };

        if (userId) {
            loadInitialTree();
        }
    }, [userId]);

    useEffect(() => {
        if (userId) {
            loadPayoutHistory();
        }
    }, [userId]);

    const loadPayoutHistory = async () => {
        try {
            LoaderHelper.loaderStatus(true);
            const response = await AuthService.payoutHistory(userId); // User-based API
            if (response?.success && Array.isArray(response?.data)) {
                setPayouts(response.data);
            }
        } catch (error) {
            console.error("Failed to fetch payout history:", error);
        } finally {
            LoaderHelper.loaderStatus(false);
        }
    };


    const TreeNode = ({ user, level = 0 }) => {
        const ulRef = useRef(null);

        const handleToggle = (e) => {
            e?.stopPropagation?.();
            const current = ulRef?.current;
            if (!current) return;

            const siblings = current?.parentElement?.parentElement?.children;
            for (let sibling of siblings || []) {
                const childUl = sibling?.querySelector?.(":scope > ul");
                if (childUl && childUl !== current) {
                    childUl?.classList?.remove?.("visible");
                }
            }

            current?.classList?.toggle?.("visible");
        };


        return (
            <li>
                <div className="node" onClick={handleToggle}>
                    {(user?.total_refer > 0 && user?.level !== 5) && (
                        <button className="toggle-icon text-success" onClick={handleToggle}>
                            Button
                        </button>
                    )}
                    <img src="/images/user.svg" alt={user?.name || "User"} />
                    <div className="user-info">
                        <div>{user?.name || "Unnamed"}</div>
                        <div>ID: {user?.id || "-"}</div>
                        <div>Email: {user?.emailId || "-"}</div>
                    </div>
                    <div className="user-info">
                        <div className={user?.kycVerified === 2 ? "text-success" : ""}>KYC Status: {user?.kycVerified === 2 ? "Verified" : "Not Verified"}</div>
                        <div className="investment-info">
                            Investment: {user?.total_invested_amount?.length || 0}
                            {user?.total_invested_amount?.length > 0 && (
                                <span className="tooltip-hover">
                                    <strong className="text-success"> View <i className="ri-eye-line"></i></strong>
                                    <div className="tooltip-content">
                                        <strong>Investment Overview</strong>
                                        {user?.total_invested_amount?.map((item, i) => (
                                            <div key={i} className="investment-item">
                                                <div>
                                                    <strong>Amount:</strong> {item?.amount?.$numberDecimal || item?.amount || 0} {item?.currency}
                                                </div>
                                                {item?.type !== 'self' ? (
                                                    <>
                                                        <div>
                                                            <strong>Self ROI%:</strong> {item?.self_roi_percent || 0}%
                                                        </div>
                                                        <div>
                                                            <strong>Your ROI%:</strong> {item?.your_upline_percent || 0}%
                                                        </div>
                                                    </>
                                                ) : <div>
                                                    <strong>Your ROI%:</strong> {item?.self_roi_percent || 0}%
                                                </div>}
                                            </div>
                                        ))}
                                    </div>
                                </span>
                            )}
                        </div>
                        <div>Investor: {user?.total_refer || "0"} <i className="ri-group-line"></i></div>
                    </div>
                </div>
                {user?.referrals?.length > 0 && (
                    <ul ref={ulRef} className={`children level-${level}`}>
                        {user?.referrals?.map((child, index) => (
                            <TreeNode key={`${child?.id}-${index}`} user={child} level={level + 1} />
                        ))}
                    </ul>
                )}
            </li>
        );
    };


    const totalSelfInvestment = flatInvestments
        .filter(inv => inv.type === 'self')
        .reduce((sum, inv) => sum + parseFloat(inv.amount || 0), 0);

    const totalDownlineInvestment = flatInvestments
        .filter(inv => inv.type === 'downline' && inv.your_upline_percent > 0)
        .reduce((sum, inv) => sum + parseFloat(inv.amount || 0), 0);


    const totalAllInvestment = totalSelfInvestment + totalDownlineInvestment;


    const ReferralTableTree = ({ nodes = [], level = 1 }) => {
        const [expandedNodes, setExpandedNodes] = useState({});

        const toggleExpand = (id) => {
            setExpandedNodes((prev) => ({
                ...prev,
                [id]: !prev[id]
            }));
        };

        const renderRows = (data, lvl) => {
            return data.flatMap((user, idx) => {
                const isExpanded = expandedNodes[user.id];
                const totalInvestment = user.total_invested_amount?.reduce((sum, inv) => sum + parseFloat(inv.amount || 0), 0) || 0;

                // Apply padding to simulate row indentation
                // const rowPadding = `${(lvl - 1) * 10}px`;
                const rowPadding = `${0}px`;
                

                const rows = [
                    <tr key={`${lvl}-${user.id}`} style={{
                        borderBottom: "1px solid #ffdc8952",
                        background: 'rgba(255, 255, 255, 0.02)'
                    }}>
                        <td className="py-3">
                            <div style={{ paddingLeft: rowPadding, display: 'flex', justifyContent: 'center' }}>
                                {user?.referrals?.length > 0 && lvl < 5 && (
                                    <div onClick={() => toggleExpand(user.id)} style={{
                                        cursor: "pointer",
                                        fontSize: '1.2rem',
                                        color: '#ffdc88',
                                        fontWeight: 'bold'
                                    }}>
                                        {isExpanded ? "−" : "+"}
                                    </div>
                                )}
                            </div>
                        </td>
                        <td className="py-3"><div style={{ paddingLeft: rowPadding }}>{idx + 1}</div></td>
                        <td className="py-3"><div style={{ paddingLeft: rowPadding }}>{user.name}</div></td>
                        <td className="py-3"><div style={{ paddingLeft: rowPadding }}>{user.emailId}</div></td>
                        {/* <td><div style={{ paddingLeft: rowPadding }}>{user.level}</div></td> */}
                        <td className="py-3">
                            <div style={{ paddingLeft: rowPadding }} className={user.kycVerified === 2 ? "text-success" : "text-danger"}>
                                {user.kycVerified === 2 ? "Verified" : "Not Verified"}
                            </div>
                        </td>
                        <td className="py-3"><div style={{ paddingLeft: rowPadding }}>{user.total_refer || 0}</div></td>
                        <td className="py-3"><div style={{ paddingLeft: rowPadding }}>₹{totalInvestment.toLocaleString('en-IN')}</div></td>
                    </tr>
                ];

                if (isExpanded && user.referrals?.length > 0) {
                    rows.push(...renderRows(user.referrals, lvl + 1));
                }

                return rows;
            });
        };

        return <>{renderRows(nodes, level)}</>;
    };




    return (
        <div className="responsive_scroll referralprogrambg px-3 pt-0">
            <div className="container-fluid">
                <div className="col-12 mb-3">
                    <div className="card py-0 border-0 rounded-3" style={{ background: 'rgba(255, 255, 255, 0.05)', backdropFilter: 'blur(10px)' }}>
                        <div className="card-body p-0">
                            <ul className="nav p-0 nav-tabs border-0 custom-nav-tabs" id="myTab" role="tablist" style={{ background: 'transparent' }}>
                                <li className="nav-item flex-fill" role="presentation">
                                    <button className="nav-link active w-100 border-0 fw-semibold" id="home-tab" data-bs-toggle="tab" data-bs-target="#home"
                                        type="button" role="tab" aria-controls="home" aria-selected="true"
                                        style={{ borderRadius: '1rem', background: 'transparent' }}>
                                        Investors List
                                    </button>
                                </li>
                                <li className="nav-item flex-fill" role="presentation">
                                    <button className="nav-link w-100 border-0 fw-semibold" id="investment-tab" data-bs-toggle="tab" data-bs-target="#investment"
                                        type="button" role="tab" aria-controls="investment" aria-selected="false"
                                        style={{ background: 'transparent' }}>
                                        Investment Summary
                                    </button>
                                </li>
                                <li className="nav-item flex-fill" role="presentation">
                                    <button className="nav-link w-100 border-0 fw-semibold" id="payout-tab" data-bs-toggle="tab" data-bs-target="#payout"
                                        type="button" role="tab" aria-controls="payout" aria-selected="false"
                                        style={{ borderRadius: '1rem ', background: 'transparent' }}>
                                        ROI Payout History
                                    </button>
                                </li>
                            </ul>
                        </div>
                    </div>
                </div>
                <div className="tab-content" id="myTabContent">

                    <div className="tab-pane fade show active" id="home" role="tabpanel" aria-labelledby="home-tab">
                        {/* <div className="col-12">
                            <div className="overview_cate_section asstes_listing_tb mt-3 new_ref_list">
                                <ul className="nav nav-tabs" id="myTab2" role="tablist">
                                    <li className="nav-item" role="presentation">
                                        <button className="nav-link active" id="table-tab" data-bs-toggle="tab" data-bs-target="#tableView"
                                            type="button" role="tab" aria-controls="tableView" aria-selected="true">Table View</button>
                                    </li>
                                    <li className="nav-item" role="presentation">
                                        <button className="nav-link" id="tree-tab" data-bs-toggle="tab" data-bs-target="#treeView"
                                            type="button" role="tab" aria-controls="treeView" aria-selected="false">Chart View</button>
                                    </li>
                                </ul>
                            </div>
                        </div> */}

                        <div className="tab-content mt-3" id="referralSubTabContent">
                            <div className="tab-pane fade show active" id="tableView" role="tabpanel" aria-labelledby="table-tab">
                                <div className="dashboard_summary2">
                                    <div className="table-responsive">
                                        <table className="table" style={{
                                            fontSize: "0.9rem",
                                            background: 'rgba(255, 255, 255, 0.03)',
                                            borderRadius: '10px',
                                            overflow: 'hidden'
                                        }}>
                                            <thead style={{
                                                background: 'rgba(255, 220, 136, 0.1)',
                                                borderBottom: '2px solid #ffdc89'
                                            }}>
                                                <tr>
                                                    <th className="py-3" style={{ width: '50px' }}></th>
                                                    <th className="py-3">S. No.</th>
                                                    <th className="py-3">Name</th>
                                                    <th className="py-3">Email</th>
                                                    {/* <th>Level</th> */}
                                                    <th className="py-3">KYC Status</th>
                                                    <th className="py-3">Investors</th>
                                                    <th className="py-3">Total Investment</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {treeRoot?.referrals?.length > 0 ? <ReferralTableTree nodes={treeRoot?.referrals || []} /> :
                                                    <tr rowSpan="5">
                                                        <td colSpan="12">
                                                            <div className="no_data_outer">
                                                                <div className="no_data_vector">
                                                                    <img src="/images/Group 1171275449 (1).svg" alt="no-data" />

                                                                </div>

                                                                <p>No Data Available</p>

                                                            </div>

                                                        </td>
                                                    </tr>
                                                }
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            </div>

                            <div className="tab-pane fade" id="treeView" role="tabpanel" aria-labelledby="tree-tab">
                                <div className="tree mt-4">
                                    <ul>
                                        {Object.keys(treeRoot)?.length > 0 ? (
                                            <TreeNode user={treeRoot} />
                                        ) : (
                                            <div className="no_data_outer">
                                                <div className="no_data_vector">
                                                    <img src="/images/Group 1171275449 (1).svg" alt="no-data" />
                                                </div>
                                                <p>No data found</p>
                                            </div>
                                        )}
                                    </ul>
                                </div>
                            </div>
                        </div>
                    </div>


                    <div className="tab-pane fade" id="investment" role="tabpanel" aria-labelledby="investment-tab">
                        <div className="card border-0 rounded-3 mb-3" style={{ background: 'rgba(255, 255, 255, 0.05)', backdropFilter: 'blur(10px)' }}>
                            <div className="card-body px-3 py-0">
                                <h5 className="text-center mb-3" style={{ fontSize: '1.3rem', fontWeight: '600' }}>
                                    💼 Total Investments Summary
                                </h5>
                                <div className="row g-3 text-center">
                                    <div className="col-md-4">
                                        <div className="p-2">
                                            <strong>Total Self Investment:</strong> ₹{totalSelfInvestment.toLocaleString('en-IN')}
                                        </div>
                                    </div>
                                    <div className="col-md-4">
                                        <div className="p-2">
                                            <strong>Total Investor's Investments:</strong> ₹{totalReferralInvestments.toLocaleString('en-IN')}
                                        </div>
                                    </div>
                                    <div className="col-md-4">
                                        <div className="p-2">
                                            <strong>Grand Total:</strong> ₹{(totalSelfInvestment + totalReferralInvestments).toLocaleString('en-IN')}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className='table-responsive'>
                            <table className="table" style={{
                                fontSize: "0.9rem",
                                background: 'rgba(255, 255, 255, 0.03)',
                                borderRadius: '10px',
                                overflow: 'hidden'
                            }}>
                                <thead style={{
                                    background: 'rgba(255, 220, 136, 0.1)',
                                    borderBottom: '2px solid #ffdc89'
                                }}>
                                    <tr>
                                        <th className="py-3">S. No.</th>
                                        <th className="py-3">Date</th>
                                        {/* <th>Investment Id</th> */}
                                        <th className="py-3">Name</th>
                                        {/* <th>Email</th> */}
                                        {/* <th>Level</th> */}
                                        <th className="py-3">Type</th>
                                        <th className="py-3">Amount</th>
                                        <th className="py-3">Currency</th>
                                        <th className="py-3">ROI%</th>
                                        <th className="py-3">Tenure</th>
                                        {/* <th>Your ROI%</th> */}
                                    </tr>
                                </thead>
                                <tbody>
                                    {flatInvestments && flatInvestments.filter(inv => (inv.type === "self" || inv.your_upline_percent > 0)).length > 0 ? (
                                        flatInvestments.filter(inv => (inv.type === "self" || inv.your_upline_percent > 0))
                                            .map((inv, idx) => (

                                                <tr key={idx} style={{
                                                    borderBottom: "1px solid #ffdc8952",
                                                    background: 'rgba(255, 255, 255, 0.02)'
                                                }}>
                                                    <td className="py-3">{idx + 1}</td>
                                                    <td className="py-3">{moment(inv.date).format("DD-MM-YYYY")} </td>
                                                    {/* <td>{inv.investmentId}</td> */}
                                                    <td className="py-3">{inv.userName}</td>
                                                    {/* <td>{inv.email}</td> */}
                                                    {/* <td>{inv.level || "---"}</td> */}
                                                    <td className="py-3 text-capitalize">{inv.type}</td>
                                                    <td className="py-3">₹{inv.amount.toLocaleString('en-IN')}</td>
                                                    <td className="py-3">{inv.currency}</td>
                                                    <td className="py-3">{inv.type === "self" ? inv.self_roi_percent : inv.your_upline_percent}%</td>
                                                    <td className="py-3">1 Year</td>
                                                    {/* <td className={inv.status === "ACTIVE" ? "text-success" : inv.status === "COMPLETED" ? "text-warning" : "text-danger"}>{inv.status}</td> */}
                                                    {/* <td>{inv.your_upline_percent || "---"}%</td> */}
                                                </tr>
                                            ))
                                    ) : (
                                        <tr>
                                            <td colSpan="8" className="text-center py-4">
                                                <div className="no_data_outer">
                                                    <div className="no_data_vector">
                                                        <img src="/images/Group 1171275449 (1).svg" alt="no-data" /></div>
                                                    <p>No Investment History</p>
                                                </div>

                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>

                    <div className="tab-pane fade" id="payout" role="tabpanel" aria-labelledby="payout-tab">
                        <div className="card border-0 rounded-3 mb-3" style={{ background: 'rgba(255, 255, 255, 0.05)', backdropFilter: 'blur(10px)' }}>
                            <div className="card-body p-3 pt-4">
                                <h5 className="mb-3 text-center" style={{ fontSize: '1.3rem', fontWeight: '600' }}>
                                    💰 ROI Payout Summary
                                </h5>
                            <div className="row g-2 mb-3">
                                <div className="col-md-4">
                                    <div className="card border-0 h-100 py-0" >
                                        <div className="card-body text-center p-2">
                                            <h6 className="text-muted mb-1" style={{ fontSize: '0.85rem' }}>Total Payouts</h6>
                                            <h4 className="mb-0 fw-bold" style={{ fontSize: '1.5rem' }}>₹{payouts.reduce((sum, p) => sum + parseFloat(p.amount || 0), 0).toLocaleString('en-IN')}</h4>
                                        </div>
                                    </div>
                                </div>
                                <div className="col-md-4">
                                    <div className="card border-0 h-100 py-0" >
                                        <div className="card-body text-center p-2">
                                            <h6 className="text-muted mb-1" style={{ fontSize: '0.85rem' }}>Self ROI Received</h6>
                                            <h4 className="mb-0 fw-bold" style={{ fontSize: '1.5rem' }}>₹{payouts.filter(p => p.type === "Self").reduce((sum, p) => sum + parseFloat(p.amount || 0), 0).toLocaleString('en-IN')}</h4>
                                        </div>
                                    </div>
                                </div>
                                <div className="col-md-4">
                                    <div className="card border-0 h-100 py-0" >
                                        <div className="card-body text-center p-2">
                                            <h6 className="text-muted mb-1" style={{ fontSize: '0.85rem' }}>Investors ROI Received</h6>
                                            <h4 className="mb-0 fw-bold" style={{ fontSize: '1.5rem' }}>₹{payouts.filter(p => p.type === "Downline").reduce((sum, p) => sum + parseFloat(p.amount || 0), 0).toLocaleString('en-IN')}</h4>
                                        </div>
                                    </div>
                                </div>
                            </div>

                                <div className='table-responsive'>
                                    <table className="table table-hover" style={{ fontSize: "0.9rem" }}>
                                    <thead style={{ background: 'rgba(255, 255, 255, 0.08)' }}>
                                        <tr>
                                            <th className="py-2">S. No.</th>
                                            <th className="py-2">Date</th>
                                            <th className="py-2">Batch Id</th>
                                            <th className="py-2">From</th>
                                            <th className="py-2">Type</th>
                                            <th className="py-2">Currency</th>
                                            <th className="py-2">ROI%</th>
                                            <th className="py-2">Amount</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {payouts.length > 0 ? (() => {
                                            // Group payouts by batchID
                                            let groupedPayouts = payouts.reduce((acc, payout) => {
                                                const batchId = payout.batchID || "No Batch";
                                                if (!acc[batchId]) {
                                                    // acc[batchId][data] = [];
                                                    acc[batchId] = { data: [], date: payout.payoutDate };
                                                }
                                                acc[batchId].data.push(payout);
                                                return acc;
                                            }, {});

                                            let serialNumber = 1;
                                            const rows = [];
                                            const sortedEntries = Object.entries(groupedPayouts).sort(
                                                ([, a], [, b]) => new Date(b.date) - new Date(a.date)
                                            );

                                            const _groupedPayouts = Object.fromEntries(
                                                sortedEntries.map(([key, val]) => [`batch_${key}`, val])
                                            );
                                            
                                            Object.entries(_groupedPayouts).forEach(([batchId, batchPayouts]) => {
                                                
                                                // Add batch payouts
                                                batchPayouts.data.forEach((payout, idx) => {
                                                    rows.push(
                                                        <tr key={`${batchId}-${idx}`} style={{ borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                                                            <td className="py-2">{idx + 1}</td>
                                                            <td className="py-2">{moment(payout.payoutDate).format("DD-MM-YYYY")}</td>
                                                            <td className="py-2">{payout.batchID || "-"}</td>
                                                            <td className="py-2">{payout.investorName || "ROI/Incentive from admin"}</td>
                                                            <td className="py-2">
                                                                {payout.type == "self" ? "Self" : "Investor"}
                                                            </td>
                                                            <td className="py-2">{payout.currency}</td>
                                                            <td className="py-2">{payout?.self_roi_percent ? payout?.self_roi_percent : payout?.your_upline_percent}%</td>
                                                            <td className="py-2 fw-bold">₹{parseFloat(payout.amount || 0).toLocaleString('en-IN')}</td>
                                                        </tr>
                                                    );
                                                });

                                                // Add batch total row
                                                const batchTotal = batchPayouts.data.reduce((sum, payout) => sum + parseFloat(payout.amount || 0), 0);
                                                const selfCount = batchPayouts.data.filter(p => p.type === "self").length;
                                                const investorCount = batchPayouts.data.filter(p => p.type !== "self").length;

                                                rows.push(
                                                    <tr key={`total-${batchId}`} style={{ background: 'rgba(255, 255, 255, 0.1)' }}>
                                                        <td colSpan="7" className="py-2 text-end">
                                                            <strong>Batch Total</strong>
                                                        </td>
                                                        <td className="py-2 ">₹{batchTotal.toLocaleString('en-IN')}</td>
                                                    </tr>
                                                );
                                            });

                                            return rows;
                                        })() : (
                                            <tr>
                                                <td colSpan="8" className="text-center">
                                                    <div className="no_data_outer">
                                                        <div className="no_data_vector">
                                                            <img src="/images/Group 1171275449 (1).svg" alt="no-data" />
                                                        </div>
                                                        <p>No payout history found</p>
                                                    </div>
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        </div>
    );
};

export default ReferralTree;